import { CheckIcon } from "@heroicons/react/20/solid";
import type React from "react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Base } from "../text/Text.react";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";

export type CheckboxProps = {
  onChange?: (status: boolean, e?: React.MouseEvent<HTMLInputElement>) => void;
  value?: boolean;
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  dataCy?: string;
  dataTooltip?: string | null;
} & Omit<Omit<React.ComponentPropsWithoutRef<"input">, "onChange">, "value">;

export const Checkbox = ({
  value,
  className,
  disabled = false,
  children,
  onChange,
  dataCy,
  dataTooltip,
  ...props
}: CheckboxProps) => {
  const [focused, setFocused] = useState<boolean>(false);
  const origin = useOrigin();
  return (
    <div
      {...(dataTooltip && { "data-tooltip": dataTooltip })}
      {...props}
      className="flex gap-x-4"
      onClick={(e) => {
        if (disabled || !onChange) return;

        onChange(!value, e as React.MouseEvent<HTMLInputElement>);
      }}
    >
      <div
        data-cy={dataCy}
        className={twMerge(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded border-[1.5px] text-white",
          value &&
            origin === FromURL.None &&
            "border-blue-500 bg-blue-500 hover:border-blue-600 hover:bg-blue-600",
          value &&
            origin === FromURL.Partner &&
            "hover:primary_hover border-priamary bg-primary hover:border-primary_hover",

          !value && "border-slate-100",
          !value && disabled && "pointer-events-none bg-slate-100",
          !value && !disabled && "hover:border-slate-300",
          !disabled && "cursor-pointer",
          disabled && value && "pointer-events-none border-slate-100 bg-slate-100",
          className
        )}
        tabIndex={0}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(event) => {
          if (focused && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            event.stopPropagation();
            onChange?.(!value);
          }
        }}
      >
        {value && <CheckIcon className="stroke grow text-white" />}
      </div>
      <Base>{children}</Base>
    </div>
  );
};
